import { supabase } from '@/lib/supabase';

// ===== Type Definitions (Single Responsibility: Type management) =====
export interface Notification {
  id: string;
  user_id: string | null;
  type: string;
  title: string;
  message: string;
  data?: any;
  is_read: boolean;
  is_sent: boolean;
  sent_at?: string;
  expires_at?: string;
  created_at: string;
  target_role?: string; // 'buyer', 'seller', 'all'
}

export interface UserNotificationStatus {
  id: string;
  user_id: string;
  notification_id: string;
  is_read: boolean;
  is_deleted: boolean;
  read_at?: string;
  deleted_at?: string;
  created_at: string;
}

export interface NotificationWithStatus extends Notification {
  isRead: boolean;
  isDeleted: boolean;
  readAt?: string;
  deletedAt?: string;
}

// ===== Repository Layer (Single Responsibility: Database access) =====
class NotificationRepository {
  // Fetch all notifications (public announcements + user's personal notifications)
  async fetchNotifications(userId?: string) {
    let query = supabase
      .from('notifications')
      .select('*')
      .order('created_at', { ascending: false });

    if (userId) {
      // Get public announcements (user_id is null) and user's personal notifications
      query = query.or(`user_id.is.null,user_id.eq.${userId}`);
    } else {
      // Non-logged in users only see public announcements
      query = query.is('user_id', null);
    }

    const { data, error } = await query;
    
    if (error) {
      console.error('Fetch notifications error:', error);
      throw error;
    }
    
    return data || [];
  }

  // Fetch user's notification statuses
  async fetchUserStatuses(userId: string) {
    const { data, error } = await supabase
      .from('user_notification_status')
      .select('*')
      .eq('user_id', userId);
    
    if (error) {
      console.error('Fetch user statuses error:', error);
      throw error;
    }
    
    return data || [];
  }

  // Create or update user notification status
  async upsertUserStatus(userId: string, notificationId: string, updates: Partial<UserNotificationStatus>) {
    const { data, error } = await supabase
      .from('user_notification_status')
      .upsert({
        user_id: userId,
        notification_id: notificationId,
        ...updates,
        created_at: new Date().toISOString()
      }, {
        onConflict: 'user_id,notification_id'
      })
      .select()
      .single();
    
    if (error) {
      console.error('Upsert user status error:', error);
      throw error;
    }
    
    return data;
  }

  // Delete personal notification (hard delete)
  async deletePersonalNotification(notificationId: string, userId: string) {
    const { error } = await supabase
      .from('notifications')
      .delete()
      .eq('id', notificationId)
      .eq('user_id', userId);
    
    if (error) {
      console.error('Delete personal notification error:', error);
      throw error;
    }
  }

  // 개인 알림의 is_read 플래그 업데이트 (공용 알림은 status 테이블만 사용)
  async markNotificationRead(notificationId: string, userId: string) {
    const { error } = await supabase
      .from('notifications')
      .update({
        is_read: true
      })
      .eq('id', notificationId)
      .eq('user_id', userId);

    if (error) {
      // 읽음 처리 실패는 치명적이지 않으므로 로깅만
      console.error('Update notifications.is_read error:', error);
    }
  }
}

// ===== Status Manager (Single Responsibility: Status management) =====
class NotificationStatusManager {
  private repository: NotificationRepository;

  constructor(repository: NotificationRepository) {
    this.repository = repository;
  }

  // Mark notification as read
  async markAsRead(userId: string, notificationId: string) {
    return await this.repository.upsertUserStatus(userId, notificationId, {
      is_read: true,
      read_at: new Date().toISOString()
    });
  }

  // Mark notification as deleted (soft delete for public, hard delete for personal)
  async markAsDeleted(userId: string, notificationId: string, _notification: Notification) {
    // RLS로 인해 직접 삭제가 막힐 수 있으므로, 개인/공용 모두 상태 테이블에 소프트 삭제 기록
    return await this.repository.upsertUserStatus(userId, notificationId, {
      is_deleted: true,
      deleted_at: new Date().toISOString()
    });
  }

  // Mark all as deleted for a user
  async markAllAsDeleted(userId: string, notifications: Notification[]) {
    const promises = notifications.map(notification => {
      return this.repository.upsertUserStatus(userId, notification.id, {
        is_deleted: true,
        deleted_at: new Date().toISOString()
      });
    });
    
    await Promise.all(promises);
  }
}

// ===== Transformer (Single Responsibility: Data transformation) =====
class NotificationTransformer {
  // Merge notifications with user statuses
  mergeWithStatuses(
    notifications: Notification[], 
    statuses: UserNotificationStatus[]
  ): NotificationWithStatus[] {
    // Create a map for quick status lookup
    const statusMap = new Map(
      statuses.map(status => [status.notification_id, status])
    );
    
    return notifications
      .filter(notification => {
        // Filter out soft-deleted notifications
        const status = statusMap.get(notification.id);
        return !status?.is_deleted;
      })
      .map(notification => {
        const status = statusMap.get(notification.id);
        
        return {
          ...notification,
          isRead: status?.is_read || notification.is_read || false,
          isDeleted: status?.is_deleted || false,
          readAt: status?.read_at,
          deletedAt: status?.deleted_at
        };
      });
  }
}

// ===== Main Service (Single Responsibility: Business logic orchestration) =====
class NotificationService {
  private repository: NotificationRepository;
  private statusManager: NotificationStatusManager;
  private transformer: NotificationTransformer;

  constructor() {
    this.repository = new NotificationRepository();
    this.statusManager = new NotificationStatusManager(this.repository);
    this.transformer = new NotificationTransformer();
  }

  // Get notifications with user-specific statuses
  async getNotifications(userId?: string) {
    try {
      console.log('[NotificationService] Getting notifications for user:', userId);
      
      // If no userId provided, try to get from auth
      if (!userId) {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          userId = user.id;
          console.log('[NotificationService] Using auth user ID:', userId);
        }
      }
      
      // Fetch base notifications
      const notifications = await this.repository.fetchNotifications(userId);
      
      // If no user, return public notifications only
      if (!userId) {
        return {
          success: true,
          data: notifications.map(n => ({
            ...n,
            isRead: n.is_read || false,
            isDeleted: false,
            readAt: undefined,
            deletedAt: undefined
          }))
        };
      }
      
      // Get user role from users table
      const { data: userData } = await supabase
        .from('users')
        .select('role')
        .eq('id', userId)
        .single();
      
      const userRole = userData?.role || 'buyer';
      console.log('[NotificationService] User role:', userRole);
      
      // Filter notifications by target_role
      const filteredNotifications = notifications.filter(n => {
        // Always show notifications without target_role (backward compatibility)
        if (!n.target_role) return true;
        // Show if target_role is 'all' or matches user's role
        return n.target_role === 'all' || n.target_role === userRole;
      });
      
      // Fetch user's statuses
      const statuses = await this.repository.fetchUserStatuses(userId);
      
      // Merge and transform
      const notificationsWithStatus = this.transformer.mergeWithStatuses(
        filteredNotifications,
        statuses
      );
      
      console.log('[NotificationService] Returning', notificationsWithStatus.length, 'notifications');
      return { success: true, data: notificationsWithStatus };
    } catch (error: any) {
      console.error('Get notifications error:', error);
      return { 
        success: false, 
        error: error.message || '알림을 불러오는 중 오류가 발생했습니다.' 
      };
    }
  }

  async getUnreadCount(userId?: string) {
    const result = await this.getNotifications(userId);
    if (!result.success || !result.data) {
      return {
        success: false,
        error: result.error || '알림 정보를 가져오지 못했습니다.'
      };
    }

    const unreadCount = result.data.reduce((count, notification) => (
      notification.isRead ? count : count + 1
    ), 0);

    return {
      success: true,
      data: unreadCount
    };
  }

  // Mark notification as read
  async markAsRead(userId: string, notificationId: string) {
    try {
      await this.statusManager.markAsRead(userId, notificationId);
      // 개인 알림에 한해 notifications 테이블의 is_read도 동기화
      await this.repository.markNotificationRead(notificationId, userId);
      return { success: true };
    } catch (error: any) {
      console.error('Mark as read error:', error);
      return { 
        success: false, 
        error: error.message || '읽음 처리 중 오류가 발생했습니다.' 
      };
    }
  }

  // Delete notification (soft delete for public, hard delete for personal)
  async deleteNotification(userId: string, notificationId: string) {
    try {
      // First, get the notification to check if it's personal or public
      const { data: notification } = await supabase
        .from('notifications')
        .select('*')
        .eq('id', notificationId)
        .single();
      
      if (!notification) {
        throw new Error('알림을 찾을 수 없습니다.');
      }
      
      await this.statusManager.markAsDeleted(userId, notificationId, notification);
      return { success: true };
    } catch (error: any) {
      console.error('Delete notification error:', error);
      return { 
        success: false, 
        error: error.message || '삭제 처리 중 오류가 발생했습니다.' 
      };
    }
  }

  // Delete all notifications for a user
  async deleteAllNotifications(userId: string) {
    try {
      // Get all user's visible notifications
      const result = await this.getNotifications(userId);
      
      if (!result.success || !result.data) {
        throw new Error('알림을 불러올 수 없습니다.');
      }
      
      // Map back to base notification type
      const notifications: Notification[] = result.data.map(n => ({
        id: n.id,
        user_id: n.user_id,
        type: n.type,
        title: n.title,
        message: n.message,
        data: n.data,
        is_read: n.is_read,
        is_sent: n.is_sent,
        sent_at: n.sent_at,
        expires_at: n.expires_at,
        created_at: n.created_at
      }));
      
      await this.statusManager.markAllAsDeleted(userId, notifications);
      return { success: true };
    } catch (error: any) {
      console.error('Delete all notifications error:', error);
      return { 
        success: false, 
        error: error.message || '전체 삭제 처리 중 오류가 발생했습니다.' 
      };
    }
  }
}

// Export singleton instance
export const notificationService = new NotificationService();
